import { ArrowRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  auditKeyProperties,
  auditValue,
  auditValueDiff,
  type ValuePart,
} from '@/features/entity-resolution/audit-values'
import type { AuditDetail, AuditMatchBasis } from '@/services/api/entity-resolution-audit'
import { cn } from '@/utils/cn'

const AUDIT = 'cases.investigate.entityResolution.audit'

function PreviewCard({
  preview,
  title,
}: {
  preview: { name: string | null; labels: string[]; properties: Record<string, unknown> }
  title: string
}) {
  const { t } = useTranslation()
  return (
    <Card size="compact" className="min-w-0 space-y-2 px-3">
      <p className="text-2xs uppercase tracking-wider text-muted-foreground">{title}</p>
      <div className="flex flex-wrap gap-1">
        {preview.labels
          .filter(label => !label.startsWith('__'))
          .map(label => (
            <Badge key={label} variant="muted">
              {label}
            </Badge>
          ))}
      </div>
      <p className="break-words text-sm font-semibold">{preview.name ?? '—'}</p>
      <dl className="space-y-1 text-xs">
        {Object.entries(preview.properties).map(([key, value]) => (
          <div key={key} className="min-w-0">
            <dt className="break-words text-muted-foreground">{key}</dt>
            <dd className="break-words font-mono">{auditValue(value)}</dd>
          </div>
        ))}
      </dl>
      {Object.keys(preview.properties).length === 0 && (
        <p className="text-xs text-muted-foreground">{t(`${AUDIT}.drawer.noProperties`)}</p>
      )}
    </Card>
  )
}

function MissingCanonicalCard({
  preview,
  fallbackName,
  onOpenAudit,
}: {
  preview: AuditDetail['canonical_preview']
  fallbackName: string | null
  onOpenAudit: (id: string) => void
}) {
  const { t } = useTranslation()
  const successor = preview.successor
  return (
    <Card size="compact" tone="warning" className="min-w-0 space-y-2 px-3">
      <p className="text-2xs uppercase tracking-wider text-muted-foreground">
        {t(`${AUDIT}.drawer.canonicalValue`)}
      </p>
      <p className="break-words text-sm font-semibold">{preview.display_name ?? fallbackName}</p>
      <p className="text-xs">{t(`${AUDIT}.drawer.canonicalMissing`)}</p>
      {successor && (
        <Button
          variant="link"
          size="sm"
          className="h-auto max-w-full whitespace-normal p-0 text-left"
          onClick={() => onOpenAudit(successor.audit_id)}
        >
          {t(`${AUDIT}.drawer.successor`, {
            name: successor.display_name ?? successor.node_id,
          })}
        </Button>
      )}
    </Card>
  )
}

export function AuditEntityComparison({
  record,
  onOpenAudit,
}: {
  record: AuditDetail
  onOpenAudit: (id: string) => void
}) {
  const { t } = useTranslation()
  const canonical = record.canonical_preview
  const preferred =
    record.match_basis?.map(item => item.property) ?? record.unique_property_hints ?? []
  return (
    <div className="mb-5 grid grid-cols-1 items-start gap-3 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
      <PreviewCard
        title={t(`${AUDIT}.drawer.mergedDeleted`)}
        preview={{
          name: record.merged_node_name,
          labels: record.merged_node_labels,
          properties: Object.fromEntries(
            auditKeyProperties(record.merged_node_properties, preferred)
          ),
        }}
      />
      <ArrowRight
        aria-hidden
        className="size-4 justify-self-center self-center rotate-90 text-muted-foreground md:rotate-0"
      />
      {canonical.exists ? (
        <PreviewCard
          title={t(`${AUDIT}.drawer.canonicalLive`)}
          preview={{
            name: canonical.display_name,
            labels: canonical.labels,
            properties: canonical.properties,
          }}
        />
      ) : (
        <MissingCanonicalCard
          preview={canonical}
          fallbackName={record.canonical_node_key}
          onOpenAudit={onOpenAudit}
        />
      )}
    </div>
  )
}

function DiffValue({ value, label }: { value: ValuePart[]; label: string }) {
  return (
    <div className="min-w-0">
      <p className="text-2xs text-muted-foreground">{label}</p>
      <p className="break-words font-mono text-xs whitespace-pre-wrap">
        {value.map((part, index) =>
          part.changed ? (
            <mark
              key={`${index}-${part.text}`}
              className="rounded-sm bg-warning/20 font-semibold text-foreground underline decoration-warning decoration-2"
            >
              {part.text}
            </mark>
          ) : (
            <span key={`${index}-${part.text}`}>{part.text}</span>
          )
        )}
      </p>
    </div>
  )
}

function MatchComparison({ item }: { item: AuditMatchBasis }) {
  const { t, i18n } = useTranslation()
  const [merged, canonical] = auditValueDiff(
    auditValue(item.merged_value),
    auditValue(item.canonical_value)
  )
  return (
    <Card size="compact" className="space-y-2 px-3">
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <span className="break-words text-xs font-semibold">
          {item.property === 'display_name' ? t(`${AUDIT}.drawer.displayName`) : item.property}
        </span>
        <Badge variant="muted" className="shrink-0">
          {t(`${AUDIT}.drawer.similarity`)}{' '}
          {new Intl.NumberFormat(i18n.language, {
            style: 'percent',
            maximumFractionDigits: 1,
          }).format(item.score)}
        </Badge>
      </div>
      <DiffValue value={merged} label={t(`${AUDIT}.drawer.mergedValue`)} />
      <DiffValue value={canonical} label={t(`${AUDIT}.drawer.canonicalValue`)} />
    </Card>
  )
}

export function AuditMergeExplanation({ record }: { record: AuditDetail }) {
  const { t } = useTranslation()
  const hints = (record.unique_property_hints ?? []).filter(
    key => key in record.merged_node_properties
  )
  return (
    <section className="mb-5 space-y-2">
      <h3 className="text-sm font-semibold">{t(`${AUDIT}.drawer.whyMerged`)}</h3>
      {record.match_basis?.length ? (
        <>
          <p className="text-xs text-muted-foreground">
            {t(`${AUDIT}.rules.${record.merge_reason}`, {
              defaultValue: record.merge_reason.replace(/_/g, ' '),
            })}
          </p>
          {record.match_basis.map(item => (
            <MatchComparison key={item.property} item={item} />
          ))}
          <p className="text-2xs text-muted-foreground">{t(`${AUDIT}.drawer.originalValues`)}</p>
        </>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">{t(`${AUDIT}.drawer.reasonNotRecorded`)}</p>
          {hints.length > 0 && (
            <div className="rounded-lg bg-muted/30 p-3 text-xs text-muted-foreground">
              <p className="mb-2">{t(`${AUDIT}.drawer.uniquePropertyHints`)}</p>
              {hints.map(key => (
                <p key={key} className="break-words font-mono">
                  {key}: {auditValue(record.merged_node_properties[key])}
                </p>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  )
}

export function AuditPropertySnapshot({ record }: { record: AuditDetail }) {
  const { t } = useTranslation()
  const matched = new Set(record.match_basis?.map(item => item.property))
  for (const item of record.match_basis ?? []) {
    if (item.property !== 'display_name') continue
    for (const [key, value] of Object.entries(record.merged_node_properties)) {
      if (auditValue(value) === auditValue(item.merged_value)) matched.add(key)
    }
  }
  return (
    <section className="mt-5 space-y-2">
      <h3 className="text-2xs font-medium uppercase tracking-wider text-muted-foreground">
        {t(`${AUDIT}.drawer.propertiesSnapshot`)}
      </h3>
      <div className="rounded-xl border border-border/60 p-2">
        {Object.entries(record.merged_node_properties).map(([key, value]) => (
          <div
            key={key}
            className={cn(
              'grid grid-cols-1 gap-1 rounded-md p-2 text-xs md:grid-cols-[136px_minmax(0,1fr)] md:gap-3',
              matched.has(key)
                ? 'bg-primary/10 font-medium text-foreground'
                : 'text-muted-foreground'
            )}
          >
            <span className="break-words">{key}</span>
            <span className="break-words font-mono">{auditValue(value)}</span>
          </div>
        ))}
        {Object.keys(record.merged_node_properties).length === 0 && (
          <p className="p-1 text-xs text-muted-foreground">{t(`${AUDIT}.drawer.noProperties`)}</p>
        )}
      </div>
    </section>
  )
}
